import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';

import { ReactComponent as FacilityArrow } from '../../../assets/icons/profile/facilityNavigate.svg';
import { ReactComponent as PriceDown } from '../../../assets/icons/profile/price-down.svg';
import { ReactComponent as PriceUp } from '../../../assets/icons/profile/price-up.svg';
import { ReactComponent as ScrollLeftArrow } from '../../../assets/icons/profile/scroll-left-arrow.svg';
import { useAppBranding } from '../../../config/branding';
import { RevenueCardData } from '../../../interface/profile.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import profile from '../../../utils/api/profile';

/**
 * View the revenue history list for scroll function
 * @returns
 */
function RevenueHistory() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation(['profile', 'sharedComp']);
  const dispatch = useAppDispatch();
  const sliderReference = useRef<Slider>(null);
  const settings = {
    infinite: false,
    slidesToShow: 5, // Show 5 cards at a time
    slidesToScroll: 2,
    autoplay: false,
    autoplaySpeed: 2000,
    arrows: false, // We will handle arrows manually
    speed: 500,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
    rtl: i18n.dir() === 'rtl',
  };

  const [revenueCardData, setRevenueCardData] = useState<RevenueCardData[]>([]);

  /**
   * moveLeft
   */
  const moveLeft = () => {
    if (sliderReference.current) {
      sliderReference.current.slickPrev(); // Moves to previous slide
    }
  };

  /**
   * Function to handle moving the slider to the right
   */
  const moveRight = () => {
    if (sliderReference.current) {
      sliderReference.current.slickNext(); // Moves to next slide
    }
  };

  /**
   * Function to fetch revenue history
   */
  const handleFetchRevenueHistory = async () => {
    try {
      const response = await profile.getRevenueHistory();
      if (response.data.length) {
        setRevenueCardData(
          response.data.map((revenue: any) => ({
            stationTitle: revenue?.chargingStation?.name || '-',
            stationNumber: '', // To-do -> Need to confirm with dinesh bro
            revenueAmount: revenue?.debitAmount?.toFixed(2) || 0,
            currency: branding.text.currency,
            growth: revenue?.percentageRevenueIncrease || 0,
          })),
        );
      }
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('sharedComp:somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    handleFetchRevenueHistory();
  }, []);

  return (
    <div className="revenue-history" dir={i18n.dir()}>
      <div className="revenue-history-header">
        <div className="revenue-left">
          <branding.icons.withdrawalHistoryIcon />
        </div>
        <div className="revenue-title">{t('title')}</div>
        <div className="revenue-right">
          <span
            role="presentation"
            className={`scroll-left ${i18n.dir() === 'rtl' ? 'rtl-flip' : ''}`}
            onClick={moveLeft}>
            <ScrollLeftArrow />
          </span>
          <span
            role="presentation"
            className={`scroll-right ${i18n.dir() === 'rtl' ? 'rtl-flip' : ''}`}
            onClick={moveRight}>
            <FacilityArrow />
          </span>
        </div>
      </div>
      <div className="revenue-cards">
        <div className="cards-sliders">
          <Slider
            ref={sliderReference}
            infinite={settings.infinite}
            slidesToShow={settings.slidesToShow}
            slidesToScroll={settings.slidesToScroll}
            autoplay={settings.autoplay}
            autoplaySpeed={settings.autoplaySpeed}
            arrows={settings.arrows}
            speed={settings.speed}
            responsive={settings.responsive}>
            {revenueCardData.map((item) => (
              <div className="card-content">
                <div className="card-title">
                  <div className="station-details">
                    <p className="station-title">{item.stationTitle}</p>
                    <p className="station-number">{item.stationNumber}</p>
                  </div>
                  <div className="revenue-value">
                    <p className="revenue-term">{t('monthlyRevenue')}</p>
                    <div className="revenue-amount">
                      <p>
                        <branding.icons.currencyIcon />
                      </p>
                      <p className="price">{item.revenueAmount}</p>
                      <p>{`(${t(`profile:${item.currency.toLowerCase()}`)})`}</p>
                    </div>
                  </div>
                </div>
                <div className="card-bottom">
                  <p>{`${item.growth.toFixed(2)}%`}</p>
                  <p>{t('growthDescription')}</p>
                  <span>{item.growth >= 0 ? <PriceUp /> : <PriceDown />}</span>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
}

export default RevenueHistory;
